import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { t } from "i18next";
import { RenderFormikInput } from "./../../../Components/Common/Forms/FormikInputHelper";
import {
  Button,
  Card,
  CardBody,
  CardImg,
  CardTitle,
  Col,
  Container,
  Form,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Input,
  FormGroup,
  Badge,
} from "reactstrap";
import { useMenuItemsStore } from "../../../stores/Assets/menuItems";
import { FormControlLabel, Switch } from "@mui/material";

const AddItem = ({ menuId, showAddItemModal, toggleAddItemModal }) => {
  const [showCatalog, setShowCatalog] = useState(true);
  const [catalogImg, setCatalogImg] = useState("");
  const [images, setImages] = useState([]);
  const [currentTag, setCurrentTag] = useState("");
  const { createItem, isLoading, catalog, getCatalog } = useMenuItemsStore(
    (state) => state,
  );

  const formik = useFormik({
    initialValues: {
      title: "",
      title_ar: "",
      title_fr: "",
      description: "",
      description_ar: "",
      description_fr: "",
      price: "",
      promo_price: "",
      is_promo: false,
      available: true,
      isHidden: false,
      IsListed: true,
      menu_id: menuId,
      type: "DEFAULT",
      ad_url: "",
      item_images: [],
      tags: [],
    },
    validationSchema: Yup.object({
      title: Yup.string().required(t("Required")),
      title_ar: Yup.string().optional(),
      title_fr: Yup.string().optional(),
      description: Yup.string().optional(),
      description_ar: Yup.string().optional(),
      description_fr: Yup.string().optional(),
      price: Yup.number().optional(),
      promo_price: Yup.number().optional(),
      is_promo: Yup.boolean().optional(),
      available: Yup.boolean().required(t("Required")),
      isHidden: Yup.boolean().optional(),
      IsListed: Yup.boolean().optional(),
      menu_id: Yup.number().required(t("Required")),
      type: Yup.string()
        .oneOf(["DEFAULT", "SERVICE", "AD"])
        .required(t("Required")),
      ad_url: Yup.string().when("type", {
        is: "AD",
        then: (schema) =>
          schema.required(t("Ad URL is required for advertisements")),
        otherwise: (schema) => schema.optional(),
      }),
      item_images: Yup.array().of(Yup.mixed().optional()),
      tags: Yup.array().of(Yup.string().optional()),
    }),
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("title_ar", values.title_ar);
      formData.append("title_fr", values.title_fr || "");
      formData.append("description", values.description || "");
      formData.append("description_ar", values.description_ar || "");
      formData.append("description_fr", values.description_fr || "");
      formData.append("price", parseInt(values.price) || 0);
      if (values.promo_price) {
        formData.append("promo_price", parseInt(values.promo_price));
      }
      formData.append("is_promo", values.is_promo.toString());
      formData.append("available", values.available.toString());
      formData.append("isHidden", values.isHidden.toString());
      formData.append("IsListed", values.IsListed.toString());
      formData.append("menu_id", parseInt(menuId));
      formData.append("type", values.type);
      if (values.type === "AD" && values.ad_url) {
        formData.append("ad_url", values.ad_url);
      }

      values.item_images.forEach((image, index) => {
        formData.append("item_images", image);
      });
      values.tags.forEach((tag, index) => {
        formData.append("tags", tag);
      });
      if (catalogImg !== "") {
        formData.append("catalog_img", catalogImg);
      }

      const result = await createItem(parseInt(menuId), formData);
      if (result) {
        formik.resetForm();
        toggleAddItemModal();
      }
    },
  });

  const fieldsToRender = [
    { fieldName: "title", label: "Title", fullWidth: true },
    { fieldName: "title_ar", label: "Title (AR)", fullWidth: true },
    { fieldName: "title_fr", label: "Title (FR)", fullWidth: true },
    {
      fieldName: "description",
      label: "Description",
      fullWidth: true,
    },
    {
      fieldName: "description_ar",
      label: "Description (AR)",
      fullWidth: true,
    },
    {
      fieldName: "description_fr",
      label: "Description (FR)",
      fullWidth: true,
    },
    { fieldName: "price", label: "Price", fullWidth: true },
    { fieldName: "promo_price", label: "Promo Price", fullWidth: true },
  ];

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    // Only allow 1 image
    const singleFile = files.slice(0, 1);
    formik.setFieldValue("item_images", singleFile);
    setImages(singleFile);
  };

  useEffect(() => {
    const fetchCatalog = async () => {
      await getCatalog();
    };
    fetchCatalog();
  }, [getCatalog]);

  return (
    <Modal isOpen={showAddItemModal} toggle={toggleAddItemModal}>
      <ModalHeader toggle={toggleAddItemModal}>
        {showCatalog ? t("Add from Catalog") : t("Add custom Item")}
      </ModalHeader>
      <ModalBody>
        {showCatalog ? (
          <Container className="mt-3">
            <Row className="g-3">
              <Col xs={12} sm={6} md={4}>
                <Card
                  className="btn shadow-sm d-flex flex-column h-100 btn btn-outline-light"
                  onClick={() => setShowCatalog(false)}
                  style={{ cursor: "pointer" }}
                >
                  <CardBody className="d-flex align-items-center justify-content-center">
                    <h5 className="text-center mb-0">Add New Item</h5>
                  </CardBody>
                </Card>
              </Col>

              {catalog.map((item) => (
                <Col key={item.id} xs={12} sm={6} md={4}>
                  <Card
                    className="shadow-sm d-flex flex-column h-100"
                    onClick={() => {
                      setShowCatalog(false);
                      formik.setFieldValue("title", item.title);
                      formik.setFieldValue("title_ar", item.title_ar);
                      formik.setFieldValue("title_fr", item.title_fr || "");
                      formik.setFieldValue("description", item.description);
                      formik.setFieldValue(
                        "description_ar",
                        item.description_ar,
                      );
                      formik.setFieldValue(
                        "description_fr",
                        item.description_fr || "",
                      );
                      setCatalogImg(item.image_url);
                    }}
                  >
                    <CardImg
                      top
                      src={item.image_url}
                      alt={item.title}
                      className="flex-shrink-0"
                      style={{ objectFit: "cover", height: "70%" }}
                    />
                    <CardBody className="d-flex flex-column flex-grow-1">
                      <CardTitle tag="h6" className="text-center mt-auto">
                        {item.title} - {item.title_ar}
                      </CardTitle>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>
          </Container>
        ) : (
          <Form onSubmit={formik.handleSubmit} className="d-flex flex-column">
            <Row className="g-2">
              {fieldsToRender.map((field) => (
                <Col key={field.fieldName} xs={12} md={6}>
                  {RenderFormikInput(formik, field)}
                </Col>
              ))}
            </Row>

            {/* Type Selector */}
            <FormGroup>
              <Label for="type">Item Type</Label>
              <Input
                id="type"
                name="type"
                type="select"
                value={formik.values.type}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <option value="DEFAULT">Default</option>
                <option value="SERVICE">Service</option>
                <option value="AD">Advertisement</option>
              </Input>
            </FormGroup>
            <FormGroup>
              <Label for="tags">Tags</Label>
              <div className="d-flex gap-2">
                <Input
                  id="tagInput"
                  name="tagInput"
                  type="text"
                  value={currentTag}
                  onChange={(e) => setCurrentTag(e.target.value)}
                  placeholder="Enter a tag"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (currentTag.trim()) {
                        formik.setFieldValue("tags", [
                          ...formik.values.tags,
                          currentTag.trim(),
                        ]);
                        setCurrentTag("");
                      }
                    }
                  }}
                />
                <Button
                  color="secondary"
                  type="button"
                  onClick={() => {
                    if (currentTag.trim()) {
                      formik.setFieldValue("tags", [
                        ...formik.values.tags,
                        currentTag.trim(),
                      ]);
                      setCurrentTag("");
                    }
                  }}
                >
                  Add
                </Button>
              </div>
              <div className="mt-2 d-flex flex-wrap gap-2">
                {Array.isArray(formik.values.tags) &&
                  formik.values.tags.map((tag, index) => (
                    <Badge
                      key={index}
                      color="primary"
                      className="p-2 d-flex align-items-center gap-2"
                    >
                      {tag}
                      <span
                        style={{ cursor: "pointer", fontWeight: "bold" }}
                        onClick={() => {
                          const newTags = [...formik.values.tags];
                          newTags.splice(index, 1);
                          formik.setFieldValue("tags", newTags);
                        }}
                      >
                        ×
                      </span>
                    </Badge>
                  ))}
              </div>
            </FormGroup>
            {/* Ad URL Field - Only show when type is AD */}
            {formik.values.type === "AD" && (
              <FormGroup>
                <Label for="ad_url">Advertisement URL</Label>
                <Input
                  id="ad_url"
                  name="ad_url"
                  type="url"
                  value={formik.values.ad_url}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="https://example.com"
                />
                {formik.touched.ad_url && formik.errors.ad_url && (
                  <div className="text-danger">{formik.errors.ad_url}</div>
                )}
              </FormGroup>
            )}

            {/* Available Switch */}
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.available}
                  onChange={(event) => {
                    const isAvailable = event.target.checked;
                    formik.setFieldValue("available", isAvailable);
                  }}
                />
              }
              label="Available"
              className="mt-2"
            />
            <small className="text-muted d-block mb-2">
              Note: Available=false means the item will show but it will say
              "out of stock"
            </small>

            {/* Hidden Switch */}
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.isHidden}
                  onChange={(event) => {
                    const isHidden = event.target.checked;
                    formik.setFieldValue("isHidden", isHidden);
                  }}
                />
              }
              label="Hidden"
              className="mt-2"
            />
            <small className="text-muted d-block mb-2">
              Note: Hidden means the item will not appear in the menu. Available and
              Hidden can be set independently.
            </small>

            {/* Listed Switch */}
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.IsListed}
                  onChange={(event) => {
                    formik.setFieldValue("IsListed", event.target.checked);
                  }}
                />
              }
              label="Listed"
              className="mt-2"
            />
            <small className="text-muted d-block mb-2">
              Note: Listed controls whether the item shows in station stock
              menus.
            </small>

            {/* Is Promo Switch */}
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.is_promo}
                  onChange={(event) => {
                    formik.setFieldValue("is_promo", event.target.checked);
                  }}
                />
              }
              label="Is Promo"
              className="mt-2"
            />
            <small className="text-muted d-block mb-2">
              Note: Enable this to show the promo price instead of regular price
            </small>

            {catalogImg === "" ? (
              <>
                <Label htmlFor="item_images" className="w-100 form-label mt-2">
                  {t("Item Image (1 image only)")}
                </Label>
                <input
                  type="file"
                  id="item_images"
                  name="item_images"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="bg-gradient rounded-3 form-control"
                />
                {images.length > 0 && (
                  <div className="mt-2">
                    <h6>Selected Images:</h6>
                    <ul>
                      {images.map((file, index) => (
                        <li key={index}>{file.name}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <div className="mt-3 d-flex flex-column align-items-center">
                <img
                  src={catalogImg}
                  alt="catalog"
                  style={{
                    objectFit: "cover",
                    height: "120px",
                    width: "100%",
                    maxWidth: "200px",
                  }}
                />
              </div>
            )}

            <div className="d-flex justify-content-between gap-2 mt-3">
              <Button
                onClick={() => {
                  setShowCatalog(true);
                  formik.resetForm();
                  setCatalogImg("");
                }}
              >
                Use Catalog
              </Button>
              <div className="d-flex gap-2">
                <Button type="submit" color="success" disabled={isLoading}>
                  {isLoading ? <Spinner size="sm" /> : <span>{t("Add")}</span>}
                </Button>
                <Button
                  color="danger"
                  onClick={() => {
                    toggleAddItemModal();
                    formik.resetForm();
                    setImages([]);
                  }}
                  disabled={isLoading}
                >
                  {t("Cancel")}
                </Button>
              </div>
            </div>
          </Form>
        )}
      </ModalBody>
      <ModalFooter>{/* Additional footer content can go here */}</ModalFooter>
    </Modal>
  );
};

export default AddItem;
